/**
 * Helpers to read sticky-offset CSS variables set by ElementHeightObserver.
 *
 * `--eo-header-height` is updated by a ResizeObserver on `#general-header`
 * (see ElementHeightObserver in BaseTemplate). Consumers across the app rely
 * on this variable for sticky positioning and scroll-to-element offset.
 *
 * These helpers centralize the read logic so it's not duplicated at each
 * callsite, and so the SSR safety check is consistent.
 */

const HEADER_HEIGHT_VAR = '--eo-header-height';
const ADMIN_BAR_HEIGHT_VAR = '--eo-admin-bar-height';

/**
 * Parse a CSS variable value (typically "190px") to a number of pixels.
 * Returns 0 for empty, non-numeric, or invalid values.
 */
const parseCssPx = (raw: string): number => {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Read a CSS variable from `document.body` and return its numeric pixel value.
 * SSR-safe: returns 0 when `window` is undefined.
 */
const readBodyVarPx = (varName: string): number => {
  if (typeof window === 'undefined') return 0;
  const raw = getComputedStyle(document.body).getPropertyValue(varName);
  return parseCssPx(raw);
};

/**
 * Current header height in pixels (from `--eo-header-height` on body).
 *
 * Reflects the live header height including any compact-mode toggle,
 * because ElementHeightObserver updates the variable via ResizeObserver.
 *
 * @returns header height in pixels, or 0 if SSR / variable missing / invalid
 */
export const getHeaderHeight = (): number => readBodyVarPx(HEADER_HEIGHT_VAR);

/**
 * Total sticky offset accounting for header and admin bar.
 *
 * Use this when computing scroll-to-element offsets for elements that should
 * land just below all sticky chrome (header + admin bar if logged in as admin).
 *
 * @returns header height + admin bar height in pixels (each falls back to 0)
 */
export const getStickyOffset = (): number =>
  readBodyVarPx(HEADER_HEIGHT_VAR) + readBodyVarPx(ADMIN_BAR_HEIGHT_VAR);

/**
 * Extra visual gap (in px) added above scroll-to-results targets.
 *
 * When InstantSearch scrolls back to the top of the results on a page/filter
 * change, landing flush under the header makes the page change easy to miss.
 * This gap leaves a strip of the previous viewport visible above the new
 * results so the reset is perceptually obvious.
 */
export const SCROLL_RESET_VISUAL_GAP_PX = 64;
